import React, { useMemo, useState, useEffect, useRef } from 'react';
import { atlasClient } from '../../../api/atlasClient';
import { SparkCreative } from '../types/sparkTypes';
import styles from '../styles/CreativeCard.module.css';
import tagStyles from '../styles/TagContextSelector.module.css';
// Use Material UI icons for consistency
import CircularProgress from '@mui/icons-material/AutorenewOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ImageIcon from '@mui/icons-material/Image';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { useAtlasStore } from '../../../store/atlasStore';

interface CreativeCardProps {
    creative: SparkCreative;
    size: 'small' | 'large';
    onClick: (fqn: string) => void;
    onDelete?: (fqn: string) => void;
    thumbnailUrl?: string;
    activeIterationNumber?: number;
    className?: string;
    isProcessing?: boolean;
    heroRefreshCounter?: number;
    hasUnseen?: boolean;
    hidePublishedBadge?: boolean;
    hideTypeBadge?: boolean;
    typeBadgeOverride?: React.ReactNode;
    // Label management
    availableLabels?: string[];
    onAddLabel?: (creativeFqn: string, label: string) => void;
    onRemoveLabel?: (creativeFqn: string, label: string) => void;
    isReadOnly?: boolean;
    jobStatusTone?: 'running' | 'completed' | 'failed' | null;
}

// Function to get creative type name from FQN using proper type registry
const useCreativeTypeName = (creativeTypeFqn: string): string => {
    // Get creative types from store
    const creativeTypes = useAtlasStore(state => state.sparkCreativeTypes);
    
    // Use useMemo to avoid recalculating on every render
    return React.useMemo(() => {
        // Find the creative type with matching FQN
        const creativeType = creativeTypes.find(type => type && type.fqn === creativeTypeFqn);
        
        // Return the name if found
        if (creativeType) {
            return creativeType.name;
        }
        
        // Fallback formatting if not found in types registry
        if (!creativeTypeFqn) return 'Unknown Type';
        const parts = creativeTypeFqn.split('.');
        const typeName = parts[parts.length - 1] || 'Creative';
        return typeName.charAt(0).toUpperCase() + typeName.slice(1).replace(/_/g, ' ');
    }, [creativeTypes, creativeTypeFqn]);
};

export const CreativeCard: React.FC<CreativeCardProps> = ({
    creative,
    size,
    onClick,
    onDelete,
    thumbnailUrl,
    className = '',
    isProcessing = false,
    heroRefreshCounter,
    hasUnseen = false,
    hidePublishedBadge = false,
    hideTypeBadge = false,
    typeBadgeOverride,
    availableLabels = [],
    onAddLabel,
    onRemoveLabel,
    isReadOnly = false,
    jobStatusTone = null,
}) => {
    const typeName = useCreativeTypeName(creative._creative_type_fqn);
    const isPublished = Boolean(creative.is_published);

    // Hero image URL: prop thumbnailUrl or first media slot image
    const [heroUrl, setHeroUrl] = useState<string | null>(thumbnailUrl || null);
    // State for active iteration number to display in badge
    // Load hero image from active iteration if in large mode and no thumbnailUrl
    useEffect(() => {
        if (size === 'large' && !thumbnailUrl && creative._hero_image_id) {
            const url = atlasClient.getAssetUrl(creative._hero_image_id, { full: true });
            setHeroUrl(url);
        } else if (size === 'large' && !thumbnailUrl) {
            // Fallback or clear if no hero image id is present
            setHeroUrl(null);
        }
        // Dependency on heroRefreshCounter is kept if it's intended to allow external refresh trigger
        // creative.fqn and creative._active_iteration_fqn are removed as we directly use the new props
    }, [size, thumbnailUrl, creative._hero_image_id, heroRefreshCounter]);
   
   // Fetch active iteration number when iteration FQN changes or new iteration is created
   // This useEffect is now redundant as _active_iteration_number is directly available.
   /*
   useEffect(() => {
     let cancelled = false;
     (async () => {
       try {
         const iterations = await atlasClient.getSparkIterations(creative.fqn);
         const activeFqn = creative._active_iteration_fqn || (iterations[iterations.length - 1]?.fqn);
         const iter = iterations.find(it => it.fqn === activeFqn);
         if (iter && !cancelled) {
           setCurrentActiveIterationNum(iter._iteration_number);
         }
       } catch (error) {
         console.error('Failed to refresh active iteration number', creative.fqn, error);
       }
     })();
     return () => { cancelled = true; };
   }, [creative.fqn, creative._active_iteration_fqn, heroRefreshCounter]);
   */

    // Resolve creative tag element names via Atlas dimensions
    const dimensions = useAtlasStore(state => state.dimensions);
    const elementNameByFqn = useMemo(() => {
        const map = new Map<string, string>();
        (dimensions || []).forEach(dim => {
            (dim.elements || []).forEach(el => {
                if (el?.fqn && el?.name) map.set(el.fqn, el.name);
            });
        });
        return map;
    }, [dimensions]);

    const selectedTagElementNames = useMemo(() => {
        const names: string[] = [];
        (creative.tag_pattern || []).forEach(entry => {
            (entry.element_fqns || []).forEach(fqn => {
                const name = elementNameByFqn.get(fqn);
                if (name) names.push(name);
            });
        });
        return names;
    }, [creative.tag_pattern, elementNameByFqn]);

    const renderTagBadges = (
        maxCount: number,
        variant: 'row' | 'overlayBelowLabel' | 'overlayTop' = 'row'
    ) => {
        if (!selectedTagElementNames.length) return null;
        const visible = selectedTagElementNames.slice(0, maxCount);
        const remaining = selectedTagElementNames.length - visible.length;
        return (
            <div className={
                variant === 'overlayBelowLabel'
                    ? styles.tagBadgeTopRight
                    : variant === 'overlayTop'
                        ? styles.tagBadgeTopRightFirstRow
                        : styles.tagBadgeRow
            }>
                {visible.map((name, idx) => (
                    <span
                        key={`${name}-${idx}`}
                        className={`${tagStyles.headerChip} ${tagStyles.headerChipSelected} ${styles.smallTagChip}`}
                        title={name}
                    >
                        <span className={tagStyles.headerChipLabel}>{name}</span>
                    </span>
                ))}
                {remaining > 0 && (
                    <span className={`${tagStyles.headerChip} ${styles.smallTagChip}`} title={`${remaining} more`}>
                        +{remaining}
                    </span>
                )}
            </div>
        );
    };

    const handleCardClick = () => {
        onClick(creative.fqn);
    };

    // --- Label picker state ---
    const [isLabelDropdownOpen, setIsLabelDropdownOpen] = useState(false);
    const [newLabelInput, setNewLabelInput] = useState('');
    const labelDropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown on outside click
    useEffect(() => {
        if (!isLabelDropdownOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (labelDropdownRef.current && !labelDropdownRef.current.contains(e.target as Node)) {
                setIsLabelDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isLabelDropdownOpen]);

    const creativeLabels = creative._labels || [];

    const handleLabelToggle = (e: React.MouseEvent, label: string) => {
        e.stopPropagation();
        if (creativeLabels.includes(label)) {
            onRemoveLabel?.(creative.fqn, label);
        } else {
            onAddLabel?.(creative.fqn, label);
        }
    };

    const handleAddCustomLabel = (e: React.FormEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const label = newLabelInput.trim();
        if (!label) return;
        onAddLabel?.(creative.fqn, label);
        setNewLabelInput('');
    };

    const renderLabelRow = () => {
        if (isReadOnly && creativeLabels.length === 0) return null;
        return (
            <div
                className={styles.labelRow}
                ref={labelDropdownRef}
                onClick={(e) => e.stopPropagation()}
            >
                {creativeLabels.map(label => (
                    <span key={label} className={styles.labelPill}>
                        <span className={styles.labelPillText}>{label}</span>
                        {!isReadOnly && onRemoveLabel && (
                            <button
                                className={styles.labelPillRemove}
                                onClick={(e) => { e.stopPropagation(); onRemoveLabel(creative.fqn, label); }}
                            >
                                <CloseIcon style={{ fontSize: '12px' }} />
                            </button>
                        )}
                    </span>
                ))}
                {!isReadOnly && onAddLabel && (
                    <button
                        className={styles.addLabelTrigger}
                        onClick={(e) => { e.stopPropagation(); setIsLabelDropdownOpen(open => !open); }}
                    >
                        <AddIcon style={{ fontSize: '14px' }} />
                        <span>Add label</span>
                    </button>
                )}
                {isLabelDropdownOpen && (
                    <div className={styles.cardLabelDropdown}>
                        <ul className={styles.cardLabelList}>
                            {availableLabels.map(label => (
                                <li
                                    key={label}
                                    className={styles.cardLabelItem}
                                    onClick={(e) => handleLabelToggle(e, label)}
                                >
                                    <span>{label}</span>
                                    {creativeLabels.includes(label) && (
                                        <CheckIcon style={{ fontSize: '16px', color: 'var(--blue-color, #2E90FA)' }} />
                                    )}
                                </li>
                            ))}
                        </ul>
                        <form className={styles.cardLabelAddForm} onSubmit={handleAddCustomLabel} onClick={e => e.stopPropagation()}>
                            <AddIcon style={{ fontSize: '14px', color: 'var(--text-light)' }} />
                            <input
                                type="text"
                                className={styles.cardLabelAddInput}
                                placeholder="Add custom label"
                                value={newLabelInput}
                                onChange={e => setNewLabelInput(e.target.value)}
                                onClick={e => e.stopPropagation()}
                            />
                        </form>
                    </div>
                )}
            </div>
        );
    };

    const renderFooter = (extra: React.ReactNode) => {
        const left = hideTypeBadge
            ? (typeBadgeOverride ? <>{typeBadgeOverride}</> : null)
            : <span className={styles.typeBadge}>{typeName}</span>;
        return (
            <div className={styles.footerRow}>
                {left}
                {extra}
            </div>
        );
    };

    // --- Render Small Card ---
    if (size === 'small') {
        return (
            <div
                className={`${styles.card} ${styles.small} ${isProcessing ? styles.processing : ''} ${className}`}
                onClick={handleCardClick}
                title={creative.name}
            >
                {hasUnseen && <span className={styles.unseenDotCorner} />}
                <div className={styles.smallContent}>
                    <span className={styles.name}>{creative.name}</span>
                </div>
                {/* Tag badges top-right overlay (compact) */}
                {renderTagBadges(2, 'overlayTop')}
                {renderFooter(
                    isPublished && !hidePublishedBadge ? (
                        <span className={styles.publishedBadge} aria-label="Published creative">
                            Published
                        </span>
                    ) : null
                )}
            </div>
        );
    }

    // --- Render Large Card ---
    const jobStatusIcon = () => {
        if (jobStatusTone === 'running') {
            return <CircularProgress fontSize="small" style={{color: '#2E90FA'}} />;
        }
        if (jobStatusTone === 'completed') {
            return <CheckCircleOutlineIcon fontSize="small" style={{color: '#12B76A'}} />;
        }
        if (jobStatusTone === 'failed') {
            return <ErrorOutlineIcon fontSize="small" style={{color: '#F04438'}} />;
        }
        if (creative._active_jobs && creative._active_jobs > 0) {
            return <CircularProgress fontSize="small" style={{color: '#2E90FA'}} />;
        }
        if (creative._successful_jobs && creative._successful_jobs > 0) {
            return <CheckCircleOutlineIcon fontSize="small" style={{color: '#12B76A'}} />;
        }
        if (creative._failed_jobs && creative._failed_jobs > 0) {
            return <ErrorOutlineIcon fontSize="small" style={{color: '#F04438'}} />;
        }
        return null;
    };

    return (
        <div
            className={`${styles.card} ${styles.large} ${isProcessing ? styles.processing : ''} ${className}`}
            onClick={handleCardClick}
            title={creative.name}
        >
            <div className={styles.thumbnail}>
                {heroUrl ? (
                    <img src={heroUrl} alt={`${creative.name} thumbnail`} />
                ) : (
                    <div className={styles.thumbnailPlaceholder}>
                        <ImageIcon fontSize="large" />
                    </div>
                )}
            </div>
            <div className={styles.largeContent}>
                {renderLabelRow()}
                <div className={styles.nameRow}>
                    <span className={styles.name}>{creative.name}</span>
                    <span className={styles.jobStatus}>{jobStatusIcon()}</span>
                </div>
                <div className={styles.description} title={creative.description || ''}>
                    {creative.description}
                </div>
                {/* Tag badges top-right overlay (expanded) */}
                {renderTagBadges(4, 'overlayTop')}
                {renderFooter(
                    <div className={styles.footerMeta}>
                        {hasUnseen && <span className={styles.unseenDotInline} />}
                        {isPublished && !hidePublishedBadge && (
                            <span className={styles.publishedBadge} aria-label="Published creative">
                                Published
                            </span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CreativeCard; 
